import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLanguage, HERO_URL } from "@/i18n";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  const { t } = useLanguage();

  return (
    <section id="hero" data-testid="hero-section" className="relative overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-indigo-50/60 via-[#F8F9FA] to-[#F8F9FA]" />
      <div className="absolute -top-24 -right-24 -z-10 h-[420px] w-[420px] rounded-full bg-indigo-200/40 blur-3xl" />
      <div className="absolute top-40 -left-20 -z-10 h-[320px] w-[320px] rounded-full bg-sky-200/40 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <motion.span
            {...fade(0)}
            className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-full px-4 py-1.5"
          >
            {t.hero.badge}
          </motion.span>

          <motion.h1
            {...fade(0.08)}
            className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-[1.05]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {t.hero.title}
          </motion.h1>

          <motion.p {...fade(0.16)} className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
            {t.hero.subtitle}
          </motion.p>

          <motion.div {...fade(0.24)} className="mt-9 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-cta-services"
              onClick={() => scrollTo("services")}
              className="group inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-full px-7 py-3.5 font-medium hover:bg-slate-800 transition-colors"
            >
              {t.hero.ctaPrimary}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollTo("contact")}
              className="inline-flex items-center justify-center bg-white text-slate-900 border border-slate-200 rounded-full px-7 py-3.5 font-medium hover:bg-slate-50 transition-colors"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-indigo-100/50 bg-white">
            <img src={HERO_URL} alt="Modern technology" className="w-full h-auto object-cover" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
